"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";

interface Category {
  id: string;
  slug: string;
  name: string;
}

interface ExpandableSubcategoriesProps {
  categories: Category[];
  maxRows?: number;
  typeParam?: string;
}

export function ExpandableSubcategories({
  categories,
  maxRows = 2,
  typeParam,
}: ExpandableSubcategoriesProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(categories.length);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState(false);

  useEffect(() => {
    if (!containerRef.current || categories.length === 0) return;

    const container = containerRef.current;
    const items = Array.from(container.children) as HTMLElement[];

    if (items.length === 0) return;

    // Trouver combien d'éléments tiennent dans maxRows lignes
    let count = 0;
    let currentTop = items[0]?.offsetTop || 0;
    let rowCount = 1;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.offsetTop > currentTop + 5) {
        rowCount++;
        currentTop = item.offsetTop;
      }
      if (rowCount <= maxRows) {
        count = i + 1;
      } else {
        break;
      }
    }

    const hasMore = categories.length > count;
    setNeedsTruncation(hasMore);
    // Si on a besoin de tronquer, on enlève 1 pour mettre le bouton à la place
    setVisibleCount(hasMore ? count - 1 : count);
    setMeasured(true);
  }, [categories, maxRows]);

  // Rendu identique serveur/client : toujours afficher tout, cacher visuellement le temps de mesurer
  if (!measured) {
    return (
      <div className="mb-6 relative">
        <div
          ref={containerRef}
          className="flex flex-wrap gap-2"
        >
          {categories.map((child) => (
            <Link
              key={child.id}
              href={`/categories/${child.slug}${typeParam ? `?type=${typeParam}` : ""}`}
              className="px-4 py-2 bg-white rounded-full text-sm border hover:border-secondary hover:text-secondary transition-colors"
            >
              {child.name}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  // Pas besoin de tronquer
  if (!needsTruncation) {
    return (
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {categories.map((child) => (
            <Link
              key={child.id}
              href={`/categories/${child.slug}${typeParam ? `?type=${typeParam}` : ""}`}
              className="px-4 py-2 bg-white rounded-full text-sm border hover:border-secondary hover:text-secondary transition-colors"
            >
              {child.name}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  // Version tronquée ou étendue
  return (
    <div className="mb-6">
      <div className="flex flex-wrap gap-2">
        {isExpanded ? (
          <>
            {categories.map((child) => (
              <Link
                key={child.id}
                href={`/categories/${child.slug}${typeParam ? `?type=${typeParam}` : ""}`}
                className="px-4 py-2 bg-white rounded-full text-sm border hover:border-secondary hover:text-secondary transition-colors"
              >
                {child.name}
              </Link>
            ))}
            <button
              onClick={() => setIsExpanded(false)}
              className="px-4 py-2 rounded-full text-sm border border-secondary text-secondary hover:bg-secondary hover:text-white transition-colors"
            >
              Voir moins
            </button>
          </>
        ) : (
          <>
            {categories.slice(0, visibleCount).map((child) => (
              <Link
                key={child.id}
                href={`/categories/${child.slug}${typeParam ? `?type=${typeParam}` : ""}`}
                className="px-4 py-2 bg-white rounded-full text-sm border hover:border-secondary hover:text-secondary transition-colors"
              >
                {child.name}
              </Link>
            ))}
            <button
              onClick={() => setIsExpanded(true)}
              className="px-4 py-2 rounded-full text-sm border border-secondary text-secondary hover:bg-secondary hover:text-white transition-colors"
            >
              Voir plus
            </button>
          </>
        )}
      </div>
    </div>
  );
}
